// @ts-nocheck
import { useNavigate } from 'react-router-dom';
import { YStack, XStack, Text } from 'tamagui';
import { ArrowUpRight } from '@phosphor-icons/react';
import { GridBand } from './GridBand';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Pillar two — the roster.
 *
 * Laid out as a hairline grid: cells sit on a 1px background so the gaps read as
 * carved rules rather than card margins. Nothing floats, nothing is rounded —
 * the page is an inscription, not a dashboard.
 *
 * Each entry carries the gloss for its name. The Roman naming is the one piece
 * of the identity a competitor cannot copy, and stating what each word meant is
 * what turns it from a theme into an argument.
 */
const roster = [
  {
    name: 'Torch',
    slug: 'torch',
    gloss: 'Carried ahead of the column',
    body: 'One search across Microsoft, Google, and Slack — plus a built-in productivity suite.',
  },
  {
    name: 'Statio',
    slug: 'statio',
    gloss: 'A guarded checkpoint',
    body: 'Governed MCP access for AI agents. Per-tool policies, vaulted credentials, full audit.',
  },
  {
    name: 'Denarius',
    slug: 'denarius',
    gloss: 'The coin',
    body: 'Institutional-grade portfolio analytics, on a filings pipeline built from SEC data.',
  },
  {
    name: 'JanusLedger',
    slug: 'janusledger',
    gloss: 'Sees both sides',
    body: 'Double-entry ledger infrastructure for anything that moves money.',
  },
  {
    name: 'Pilum',
    slug: 'pilum',
    gloss: 'The javelin · open source',
    body: 'One config, one CLI, every cloud. Deploys the whole portfolio.',
  },
  {
    name: 'Scuta',
    slug: 'scuta',
    gloss: 'The shields · open source',
    body: 'CLI installer that fails closed. Nothing lands without a verified checksum.',
  },
  {
    name: 'Capsa',
    slug: 'capsa',
    gloss: 'The scroll case · open source',
    body: 'Self-hosted documentation. MDX, command palette, OpenAPI reference, free to host.',
  },
  {
    name: 'Vigil',
    slug: 'vigil',
    gloss: 'The night watch · open source',
    body: 'Watches your connection and turns outages into evidence you can send an ISP.',
  },
];

export function ProductRoster() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  return (
    <YStack id="products">
      <GridBand
        eyebrow="The products"
        title="Eight products, each its own brand."
        lede="Named for what the Romans built or carried. Each sells on its own terms; none requires the others. Four are open source."
      >
        {/* 1px gap over a rule-coloured ground: the gaps become the rules. */}
        <XStack
          flexWrap="wrap"
          gap={1}
          backgroundColor="$color3"
          borderWidth={1}
          borderColor="$color3"
        >
          {roster.map((item) => (
            <YStack
              key={item.name}
              gap="$2"
              padding={isMobile ? '$4' : '$5'}
              backgroundColor="$background"
              flexGrow={1}
              flexBasis={isMobile ? '100%' : 260}
              cursor="pointer"
              position="relative"
              hoverStyle={{ backgroundColor: '$color2' }}
              onPress={() => navigate(`/products/${item.slug}`)}
            >
              {/* Tab breaking the cell corner — the affordance that says a cell
                  in a flat grid is actually a link. */}
              <YStack
                position="absolute"
                top={-1}
                right={-1}
                width={26}
                height={26}
                borderWidth={1}
                borderColor="$color3"
                backgroundColor="$background"
                alignItems="center"
                justifyContent="center"
                opacity={0}
                hoverStyle={{ opacity: 1 }}
                $group-hover={{ opacity: 1 }}
              >
                <ArrowUpRight size={12} color="var(--amber9)" />
              </YStack>

              <Text fontSize={isMobile ? '$6' : '$7'} fontWeight="600" color="$color12">
                {item.name}
              </Text>

              <Text
                fontSize="$1"
                color="$color7"
                textTransform="uppercase"
                letterSpacing={1.4}
              >
                {item.gloss}
              </Text>

              <Text fontSize="$3" color="$color8" lineHeight={21} marginTop="$1">
                {item.body}
              </Text>
            </YStack>
          ))}
        </XStack>
      </GridBand>
    </YStack>
  );
}
